import * as Dialog from '@radix-ui/react-dialog';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import { ArrowRight, Check, ClipboardPaste, Copy, Download, X } from 'lucide-react';
import { api } from '@/lib/api';
import { cn, numero } from '@/lib/utils';
import { Botao } from '@/componentes/ui/botao';
import { EstadoErro } from '@/componentes/ui/estados';
import { Esqueleto } from '@/componentes/ui/primitivos';
import { Localizador } from './Localizador';

/**
 * O detalhe de uma transferência — o destino do hiperlink do kardex.
 *
 * Uma linha do kardex responde "quanto saiu daqui". A pergunta seguinte é
 * sempre "quem autorizou isso", e até agora ela só tinha resposta abrindo o
 * Procfit. Aqui ela está a um clique: quem criou, quem aprovou, quando, e o
 * que exatamente moveu.
 *
 * As DUAS pernas do kardex levam ao mesmo lugar. A saída vem da aprovação e a
 * entrada, do recebimento — registros diferentes, mesma transferência. Quem
 * clicou numa ou noutra chega nesta mesma tela, que é o que ele queria.
 */

interface ItemDetalhe {
  produto: number;
  descricao: string | null;
  quantidade: number;
  localizadorOrigem: number | null;
  localizadorOrigemEndereco: string | null;
  localizadorDestino: number | null;
  localizadorDestinoEndereco: string | null;
}

interface Detalhe {
  transferencia: number;
  data: string | null;
  centroOrigem: number;
  centroOrigemNome: string | null;
  centroDestino: number;
  centroDestinoNome: string | null;
  observacao: string | null;
  criadoPorNome: string | null;
  aprovacao: number | null;
  aprovadoPor: string | null;
  aprovadoEm: string | null;
  recebimento: number | null;
  recebidoPor: string | null;
  recebidoEm: string | null;
  /** Os padrões do cabeçalho — o que o item sem localizador próprio herdou. */
  localizadorOrigemPadrao: number | null;
  localizadorOrigemPadraoEndereco: string | null;
  localizadorDestinoPadrao: number | null;
  localizadorDestinoPadraoEndereco: string | null;
  itens: ItemDetalhe[];
  /** Presente quando os itens vieram de "colar lista". */
  importacao: Importacao | null;
}

interface Importacao {
  em: string;
  login: string | null;
  arquivo: string | null;
  texto: string;
  linhas: {
    linha: number;
    texto: string;
    codigo: string | null;
    quantidade: number | null;
    situacao: 'ok' | 'escolhido' | 'nao_encontrado' | 'invalida' | 'descartada' | 'cabecalho';
    produto: number | null;
    via: 'codigo' | 'ean' | null;
    candidatos?: number[];
    origem?: string | null;
    destino?: string | null;
    motivo?: string | null;
  }[];
  itensEnviados: {
    produto: number;
    quantidade: number;
    localizadorOrigem?: number | null;
    localizadorDestino?: number | null;
  }[];
}

const SITUACAO: Record<Importacao['linhas'][number]['situacao'], { rotulo: string; cor: string }> = {
  ok: { rotulo: 'entrou', cor: 'text-sucesso' },
  escolhido: { rotulo: 'ambíguo — escolhido', cor: 'text-atencao' },
  nao_encontrado: { rotulo: 'não encontrado', cor: 'text-perigo' },
  invalida: { rotulo: 'inválida', cor: 'text-perigo' },
  descartada: { rotulo: 'descartada', cor: 'text-textoFraco' },
  cabecalho: { rotulo: 'cabeçalho', cor: 'text-textoFraco' },
};

function baixarTexto(nome: string, texto: string): void {
  const url = URL.createObjectURL(new Blob([texto], { type: 'text/plain;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = nome;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Os itens no formato que o "Colar lista" lê: `produto quantidade`, um por
 * linha. O mesmo produto em mais de uma linha (posições diferentes) sai
 * somado — quem cola noutra transferência quer o total, não a posição.
 */
function listaDosItens(itens: ItemDetalhe[]): string {
  const soma = new Map<number, number>();
  for (const i of itens) soma.set(i.produto, (soma.get(i.produto) ?? 0) + i.quantidade);
  return [...soma].map(([p, q]) => `${p} ${q}`).join('\r\n') + '\r\n';
}

/**
 * Exporta a lista da transferência. "Copiar" é o caminho curto: copiar daqui
 * e colar no "Colar lista" de outra transferência — devolver pelo mesmo
 * caminho, repetir um abastecimento. O TXT é para guardar ou mandar adiante.
 */
function ExportarItens({
  transferencia,
  itens,
}: {
  transferencia: number;
  itens: ItemDetalhe[];
}): JSX.Element {
  const [copiado, definirCopiado] = React.useState(false);
  const copiar = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(listaDosItens(itens));
      definirCopiado(true);
      window.setTimeout(() => definirCopiado(false), 2000);
    } catch {
      // Sem permissão de área de transferência (http, navegador antigo): o
      // TXT continua disponível ao lado.
    }
  };
  return (
    <div className="mb-2 flex items-center gap-1.5">
      <span className="rotulo mr-auto">{itens.length} item(ns)</span>
      <Botao variante="secundaria" tamanho="micro" onClick={() => void copiar()}>
        {copiado ? <Check className="h-3.5 w-3.5 text-sucesso" /> : <Copy className="h-3.5 w-3.5" />}
        {copiado ? 'Copiado' : 'Copiar lista'}
      </Botao>
      <Botao
        variante="secundaria"
        tamanho="micro"
        onClick={() => baixarTexto(`transferencia-${transferencia}.txt`, listaDosItens(itens))}
      >
        <Download className="h-3.5 w-3.5" />
        Baixar .txt
      </Botao>
    </div>
  );
}

/**
 * O rastro da importação: o que foi colado e o que a prévia fez com cada
 * linha. Fechado por padrão — é para quando alguém pergunta "de onde veio
 * isso?", não para o dia a dia.
 */
function RastroImportacao({
  transferencia,
  imp,
}: {
  transferencia: number;
  imp: Importacao;
}): JSX.Element {
  const baixar = (): void =>
    baixarTexto(`transferencia-${transferencia}-lista-colada.txt`, imp.texto);
  const fora = imp.linhas.filter(
    (l) => l.situacao === 'nao_encontrado' || l.situacao === 'invalida' || l.situacao === 'descartada',
  ).length;
  return (
    <details className="mt-3 rounded-lg border border-borda">
      <summary className="flex cursor-pointer items-center gap-2 px-3 py-2 text-[12.5px]">
        <ClipboardPaste className="h-3.5 w-3.5 text-textoFraco" />
        <span className="font-semibold">Importada de lista</span>
        <span className="text-textoFraco">
          {imp.login ?? '—'} · {quando(imp.em)} · {imp.linhas.length} linha(s)
          {fora > 0 ? ` · ${fora} não entraram` : ''}
          {imp.arquivo ? ` · ${imp.arquivo}` : ''}
        </span>
      </summary>
      <div className="space-y-2 border-t border-borda px-3 py-2">
        <table className="w-full text-left text-[11.5px]">
          <thead>
            <tr className="text-textoFraco">
              <th className="rotulo pb-1 pr-2">Lin.</th>
              <th className="rotulo pb-1 pr-2">Colado</th>
              <th className="rotulo pb-1 pr-2">Resultado</th>
              <th className="rotulo pb-1 text-right">Produto</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-borda">
            {imp.linhas.map((l, n) => (
              <tr key={`${l.linha}-${n}`}>
                <td className="tabular py-1 pr-2 font-mono text-textoFraco">{l.linha}</td>
                <td className="max-w-[220px] truncate py-1 pr-2 font-mono" title={l.texto}>
                  {l.texto}
                </td>
                <td className={cn('py-1 pr-2', SITUACAO[l.situacao].cor)}>
                  {SITUACAO[l.situacao].rotulo}
                  {l.via === 'ean' ? ' (EAN)' : ''}
                  {l.candidatos ? ` entre ${l.candidatos.join(', ')}` : ''}
                  {l.motivo && l.situacao !== 'ok' && l.situacao !== 'escolhido'
                    ? ` — ${l.motivo}`
                    : ''}
                </td>
                <td className="tabular py-1 text-right font-mono">{l.produto ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <Botao variante="secundaria" tamanho="micro" onClick={baixar}>
          <Download className="h-3.5 w-3.5" />
          Baixar o texto colado
        </Botao>
      </div>
    </details>
  );
}

function quando(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
}

function Etapa({
  rotulo,
  quem,
  em,
  documento,
}: {
  rotulo: string;
  quem: string | null;
  em: string | null;
  documento?: number | null | undefined;
}): JSX.Element {
  return (
    <div className="min-w-0 flex-1">
      <p className="rotulo">{rotulo}</p>
      <p className="truncate text-[12.5px] font-semibold" title={quem ?? ''}>
        {quem ?? '—'}
      </p>
      <p className="tabular text-[11px] text-textoFraco">
        {quando(em)}
        {documento ? ` · nº ${documento}` : ''}
      </p>
    </div>
  );
}

export function DetalheTransferencia({
  transferencia,
  aoFechar,
}: {
  transferencia: number | null;
  aoFechar: () => void;
}): JSX.Element {
  const consulta = useQuery<Detalhe>({
    queryKey: ['transferencia-detalhe', transferencia],
    queryFn: () => api.get<Detalhe>(`/api/transferencias/${transferencia}`),
    enabled: transferencia !== null,
    staleTime: 60_000,
  });

  const d = consulta.data;

  return (
    <Dialog.Root open={transferencia !== null} onOpenChange={(v) => !v && aoFechar()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 flex max-h-[min(90vh,720px)] w-[min(640px,94vw)] -translate-x-1/2 -translate-y-1/2 flex-col rounded-xl border border-borda bg-superficie shadow-2xl">
          <header className="flex shrink-0 items-start gap-3 border-b border-borda px-4 py-3">
            <div className="min-w-0 flex-1">
              <p className="rotulo">Transferência entre centros</p>
              <Dialog.Title className="tabular font-mono text-[20px] font-bold leading-tight">
                {transferencia}
              </Dialog.Title>
            </div>
            <Dialog.Close asChild>
              <Botao variante="fantasma" tamanho="pequeno">
                <X className="h-4 w-4" />
              </Botao>
            </Dialog.Close>
          </header>

          {consulta.isLoading ? (
            <Esqueleto className="m-4 h-64" />
          ) : consulta.isError ? (
            <div className="p-4">
              <EstadoErro erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} />
            </div>
          ) : d ? (
            <>
              <div className="shrink-0 space-y-3 border-b border-borda px-4 py-3">
                <div className="flex flex-wrap items-center gap-2 text-[13px]">
                  <span className="leading-tight">
                    <span className="rotulo block">Sai de</span>
                    <span className="tabular font-mono font-bold">{d.centroOrigem}</span>
                    <span className="block text-[11px] text-textoFraco">
                      {d.centroOrigemNome ?? '—'}
                    </span>
                    {d.localizadorOrigemPadrao ? (
                      <span
                        className="block font-mono text-[11px]"
                        title="Localizador de origem padrão do cabeçalho"
                      >
                        padrão {d.localizadorOrigemPadraoEndereco ?? d.localizadorOrigemPadrao}
                      </span>
                    ) : null}
                  </span>
                  <ArrowRight className="h-4 w-4 shrink-0 text-textoFraco" />
                  <span className="leading-tight">
                    <span className="rotulo block">Entra em</span>
                    <span className="tabular font-mono font-bold">{d.centroDestino}</span>
                    <span className="block text-[11px] text-textoFraco">
                      {d.centroDestinoNome ?? '—'}
                    </span>
                    {d.localizadorDestinoPadrao ? (
                      <span
                        className="block font-mono text-[11px]"
                        title="Localizador de destino padrão do cabeçalho"
                      >
                        padrão {d.localizadorDestinoPadraoEndereco ?? d.localizadorDestinoPadrao}
                      </span>
                    ) : null}
                  </span>
                </div>

                {d.observacao ? (
                  <p className="text-[12.5px]">
                    <span className="rotulo">Observação</span> {d.observacao}
                  </p>
                ) : null}

                {/*
                  As três etapas em linha, na ordem do fluxo. É a resposta ao
                  "quem autorizou" que o kardex não dá — e mostra também quando
                  a transferência ainda não passou por alguma delas.
                */}
                <div className="flex flex-wrap gap-4 border-t border-borda pt-2.5">
                  <Etapa rotulo="Criada por" quem={d.criadoPorNome} em={d.data} />
                  <Etapa
                    rotulo="Aprovada por"
                    quem={d.aprovadoPor}
                    em={d.aprovadoEm}
                    documento={d.aprovacao}
                  />
                  <Etapa
                    rotulo="Recebida por"
                    quem={d.recebidoPor}
                    em={d.recebidoEm}
                    documento={d.recebimento}
                  />
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
                {d.itens.length > 0 && transferencia !== null ? (
                  <ExportarItens transferencia={transferencia} itens={d.itens} />
                ) : null}
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-borda text-textoFraco">
                      <th className="rotulo pb-1.5 pr-2">Produto</th>
                      <th className="rotulo pb-1.5 pr-2">Descrição</th>
                      <th className="rotulo pb-1.5 pr-2 text-right">Qtde</th>
                      <th className="rotulo pb-1.5 pr-2">Sai de</th>
                      <th className="rotulo pb-1.5">Entra em</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-borda">
                    {d.itens.map((i, n) => (
                      <tr key={`${i.produto}-${n}`}>
                        <td className="tabular py-1.5 pr-2 font-mono text-[12px] font-bold">
                          {i.produto}
                        </td>
                        <td
                          className="max-w-[280px] truncate py-1.5 pr-2 text-[12px]"
                          title={i.descricao ?? ''}
                        >
                          {i.descricao ?? '—'}
                        </td>
                        <td className="tabular py-1.5 pr-2 text-right text-[12px] font-bold">
                          {numero(i.quantidade)}
                        </td>
                        <td className="max-w-[150px] py-1.5 pr-2 text-[11.5px]">
                          <Localizador
                            ponta="origem"
                            p={
                              i.localizadorOrigem
                                ? {
                                    localizador: i.localizadorOrigem,
                                    endereco:
                                      i.localizadorOrigemEndereco ?? String(i.localizadorOrigem),
                                    ativo: true,
                                  }
                                : null
                            }
                          />
                        </td>
                        <td className="max-w-[150px] py-1.5 text-[11.5px]">
                          <Localizador
                            ponta="destino"
                            p={
                              i.localizadorDestino
                                ? {
                                    localizador: i.localizadorDestino,
                                    endereco:
                                      i.localizadorDestinoEndereco ?? String(i.localizadorDestino),
                                    ativo: true,
                                  }
                                : null
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {d.importacao && transferencia !== null ? (
                  <RastroImportacao transferencia={transferencia} imp={d.importacao} />
                ) : null}
              </div>
            </>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
